import { ChangeEvent, forwardRef, MouseEvent, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { format, isAfter } from 'date-fns';
import { classNames } from 'primereact/utils';
import {
  ActiveBundlePageType,
  ActiveBundlePageTypeKeys,
  TimelineChronologyEntry,
  TimelineChronologyType,
  TimelineChronologyTypeKeys,
  selectCaseIdData,
  useAppSelector,
} from '@/store';
import {
  Button,
  Calendar,
  CalendarProps,
  Checkbox,
  CheckboxChangeEvent,
  ArrowDown,
  ArrowUpIcon,
  CloseIcon,
  CommentIcon,
  QuoteOutlineIcon,
  YellowWarningIcon,
  ConfiguredCalendar,
  ConfirmationDialog,
  Dropdown,
  DropdownChangeEvent,
  InputText,
  InputTextarea,
  InsertableListVirtuoso,
  InsertableListVirtuosoRef,
  TrashIcon,
} from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { SymbolPickerToolbar } from '@/ui/components/SymbolPickerToolbar';
import { useDictation } from '@/hooks';
import { spliceDictatedText } from '@/ui/pages/BundlePage/components/addToChronologyUtils';
import {
  EntryData,
  EntryDataKey,
  EntryDataValue,
  ProviderOption,
  useTimelineChronologyBundleNavigate,
  useTimelineChronologyDelete,
  useTimelineChronologySlotIn,
  useEntryReordering,
  useDateHeaderLogic,
} from '../hooks';
import { AccessControlAction, TIMELINE_CHRONOLOGY_LIST_DELETE_ITEM } from '@/constants';
import { sortPagesByBundleType } from '../utils';
import { useAccessControl } from '@/hooks';
import { DateHeaderWithButton } from './DateHeaderWithButton';

interface ListProps {
  activeBundlePageType: ActiveBundlePageTypeKeys;
  activeTimelineChronologyType: TimelineChronologyTypeKeys | null;
  currentListEntries: TimelineChronologyEntry[];
  mixedListEntries?: (string | TimelineChronologyEntry | null | undefined)[];
  selectedEntryIds: string[];
  newEntryIndex: number | null;
  editEntryIndex: number | null;
  entryData: EntryData;
  providersDropdownOptions: ProviderOption[];
  isPagesInputError: boolean;
  isValidEntryData: boolean;
  handleSetActiveEntryIndex: (index: number | null) => void;
  handleChangeSelectedEntryIds: (id: string, checked?: boolean) => void;
  handleAddNewEntry: (index: number) => void;
  handleChangeEntryData: (key: EntryDataKey, value: EntryDataValue) => void;
  handleCreateNewEntry: () => void;
  handleCancelCreateNewEntry: () => void;
  handleEditEntryData: (entry: TimelineChronologyEntry, index: number) => void;
  handleCancelEditEntry: () => void;
  handleSaveExistingEntry: () => void;
  /** Entry to briefly highlight after a cross-tab jump.
   *
   *  `token` alternates each trigger (parity flips) so the row's CSS
   *  class swaps between two animation-name variants — necessary
   *  because otherwise clicking the same already-focused entry
   *  wouldn't restart the animation (React sees the same id, and CSS
   *  won't replay an animation whose class name hasn't changed).
   *  Cleared automatically by the parent after ~6s. */
  flashState?: { id: string; token: number } | null;
}

export const List = forwardRef((props: ListProps, ref: Ref<InsertableListVirtuosoRef>) => {
  const {
    activeBundlePageType,
    activeTimelineChronologyType,
    currentListEntries,
    mixedListEntries,
    selectedEntryIds,
    newEntryIndex,
    editEntryIndex,
    entryData,
    providersDropdownOptions,
    isPagesInputError,
    isValidEntryData,
    handleSetActiveEntryIndex,
    handleChangeSelectedEntryIds,
    handleAddNewEntry,
    handleChangeEntryData,
    handleCreateNewEntry,
    handleCancelCreateNewEntry,
    handleEditEntryData,
    handleCancelEditEntry,
    handleSaveExistingEntry,
    flashState,
  } = props;

  const { handlePageClick, isViewBundles } = useTimelineChronologyBundleNavigate();
  const { handleTimelineChronologyDelete } = useTimelineChronologyDelete();
  const [isPagesInputFocused, setIsPagesInputFocused] = useState(false);
  const extractTextRef = useRef<HTMLTextAreaElement | null>(null);
  const commentTextRef = useRef<HTMLTextAreaElement | null>(null);

  const insertSymbol = useCallback(
    (symbol: string) => {
      const textarea = extractTextRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = entryData.summary || '';
        const newText = currentText.slice(0, start) + symbol + currentText.slice(end);
        handleChangeEntryData('summary', newText);
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + symbol.length;
          textarea.focus();
        });
      } else {
        handleChangeEntryData('summary', (entryData.summary || '') + symbol);
      }
    },
    [entryData.summary, handleChangeEntryData],
  );

  const insertSymbolIntoComment = useCallback(
    (symbol: string) => {
      const textarea = commentTextRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = entryData.comment || '';
        const newText = currentText.slice(0, start) + symbol + currentText.slice(end);
        handleChangeEntryData('comment', newText);
        requestAnimationFrame(() => {
          textarea.selectionStart = textarea.selectionEnd = start + symbol.length;
          textarea.focus();
        });
      } else {
        handleChangeEntryData('comment', (entryData.comment || '') + symbol);
      }
    },
    [entryData.comment, handleChangeEntryData],
  );

  // Live-splice dictation for the Extract (summary) field and Comment field.
  // Same pattern as the Add-to-Chronology dialog: snapshot base+caret on
  // start, splice the growing transcript in each render while listening,
  // roll back on cancel.
  const extractDictationSnapshotRef = useRef<{ base: string; caret: number } | null>(null);
  const extractDictation = useDictation({
    onStart: () => {
      const textarea = extractTextRef.current;
      const base = entryData.summary ?? '';
      const caret = textarea ? textarea.selectionStart : base.length;
      extractDictationSnapshotRef.current = { base, caret };
    },
    onCancel: () => {
      const snapshot = extractDictationSnapshotRef.current;
      if (!snapshot) return;
      handleChangeEntryData('summary', snapshot.base);
      extractDictationSnapshotRef.current = null;
    },
    onCommit: () => {
      extractDictationSnapshotRef.current = null;
    },
  });

  useEffect(() => {
    if (!extractDictation.listening) return;
    const snapshot = extractDictationSnapshotRef.current;
    if (!snapshot) return;
    const newText = spliceDictatedText(snapshot.base, snapshot.caret, extractDictation.transcript);
    if (newText !== entryData.summary) {
      handleChangeEntryData('summary', newText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractDictation.transcript, extractDictation.listening]);

  const commentDictationSnapshotRef = useRef<{ base: string; caret: number } | null>(null);
  const commentDictation = useDictation({
    onStart: () => {
      const textarea = commentTextRef.current;
      const base = entryData.comment ?? '';
      const caret = textarea ? textarea.selectionStart : base.length;
      commentDictationSnapshotRef.current = { base, caret };
    },
    onCancel: () => {
      const snapshot = commentDictationSnapshotRef.current;
      if (!snapshot) return;
      handleChangeEntryData('comment', snapshot.base);
      commentDictationSnapshotRef.current = null;
    },
    onCommit: () => {
      commentDictationSnapshotRef.current = null;
    },
  });

  useEffect(() => {
    if (!commentDictation.listening) return;
    const snapshot = commentDictationSnapshotRef.current;
    if (!snapshot) return;
    const newText = spliceDictatedText(snapshot.base, snapshot.caret, commentDictation.transcript);
    if (newText !== entryData.comment) {
      handleChangeEntryData('comment', newText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentDictation.transcript, commentDictation.listening]);

  const caseId = useAppSelector(selectCaseIdData);
  const isEditTimelineChronology = useAccessControl(AccessControlAction.EDIT_TIMELINE_CHRONOLOGY);

  const isChronologyActive = activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY;
  const isTimelineActive = activeTimelineChronologyType === TimelineChronologyType.TIMELINE;

  // Use custom hooks
  const { entryIdToMixedIndexMap, entryIdToIndexMap, shouldShowDateHeaderForEntry, renderDateHeader } =
    useDateHeaderLogic({
      mixedListEntries,
      currentListEntries,
      isChronologyActive,
    });

  const { handleSlotIntoOriginalRecords } = useTimelineChronologySlotIn({
    caseId,
    isChronologyActive,
    isTimelineActive,
    mixedListEntries,
    entryIdToMixedIndexMap,
  });

  const { shouldShowUpArrow, shouldShowDownArrow, shouldShowArrows, handleReorder } = useEntryReordering({
    caseId,
    isChronologyActive,
    isTimelineActive,
    currentListEntries,
    mixedListEntries,
  });

  const renderDateHeaderWithButton = (dateString: string | null, entryId: string) => {
    if (!dateString) return null;

    const headerText = renderDateHeader(dateString);

    return (
      <DateHeaderWithButton
        headerText={headerText}
        entryId={entryId}
        isChronologyActive={isChronologyActive}
        onSlotClick={handleSlotIntoOriginalRecords}
      />
    );
  };

  return (
    <>
      <ConfirmationDialog
        id={TIMELINE_CHRONOLOGY_LIST_DELETE_ITEM}
        style={{ width: '280px', borderRadius: '8px', border: '1px solid #e1e1e0', overflow: 'hidden' }}
        contentClassName="!p-0"
        headerClassName="!hidden"
      >
        {(_, { accept, reject }) => {
          return (
            <div className="relative p-[24px]">
              <button
                type="button"
                aria-label="Close"
                onClick={reject}
                className="absolute top-[24px] right-[24px] cursor-pointer rounded p-1 focus:outline-none"
              >
                <CloseIcon width={12} height={12} />
              </button>
              <div className="mt-[16px] flex flex-col items-center">
                <YellowWarningIcon width={48} height={48} />
                <div className="text-fletchers-black-900 mt-[16px] text-[18px]/[18px] font-[600]">Are you sure?</div>
              </div>
              <div className="mt-[16px]">
                <p className="text-center text-[14px]/[20px]">1 entry will be deleted.</p>
              </div>
              <div className="mt-[24px] flex justify-end gap-2">
                <OutlineButton className="!h-[32px]" onClick={reject}>
                  Cancel
                </OutlineButton>
                <GreenButton className="!h-[32px]" onClick={accept}>
                  Confirm
                </GreenButton>
              </div>
            </div>
          );
        }}
      </ConfirmationDialog>

      <div className="[&>div]:!bg-fletchers-gray-25 h-full w-full">
        <InsertableListVirtuoso
          ref={ref}
          items={currentListEntries}
          onChange={handleAddNewEntry}
          handleSetActiveItemIndex={handleSetActiveEntryIndex}
          style={{
            overflowY: 'scroll',
          }}
          renderItem={(entry: TimelineChronologyEntry, index) => {
            const dateHeaderText = shouldShowDateHeaderForEntry(entry.id);
            const currentIndex = entryIdToIndexMap.get(entry.id);

            // Edit mode rendering
            if (index === newEntryIndex || index === editEntryIndex) {
              return (
                <div className="px-3">
                  {dateHeaderText && renderDateHeaderWithButton(dateHeaderText, entry.id)}
                  <div
                    key={entry.id}
                    className={classNames(
                      'grid grid-cols-[200px_1fr_240px] gap-6 text-[14px]',
                      'border-fletchers-gray-110 rounded-lg border border-l-[3px] bg-white p-6',
                      {
                        'border-l-fletchers-green-300': !entry.containsAdditionalRecords,
                        'border-l-[#3BB8C9]': entry.containsAdditionalRecords,
                      },
                    )}
                  >
                    {/* Left column: Date, Time, Location, Pages */}
                    <div className="flex w-[200px] flex-col gap-4">
                      <div>
                        <h4 className="mb-2 text-sm font-semibold">Date</h4>
                        <ConfiguredCalendar
                          showOnFocus
                          value={entryData.date}
                          onChange={(event: CalendarProps<'single', Date | (Date | null)[]>) =>
                            handleChangeEntryData('date', event.value as Date)
                          }
                          className="w-full rounded-sm bg-white [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
                        />
                      </div>
                      <div>
                        <h4 className="mb-2 text-sm font-semibold">
                          Time <span className="font-light">(optional)</span>
                        </h4>
                        <Calendar
                          value={
                            entryData.time && isAfter(entryData.time, new Date('1970-01-01T00:00:00'))
                              ? entryData.time
                              : null
                          }
                          onChange={(event: CalendarProps<'single', Date | (Date | null)[]>) => {
                            const timeValue = event.value as Date;
                            handleChangeEntryData('time', timeValue);
                          }}
                          className="w-full rounded-sm bg-white"
                          timeOnly
                        />
                      </div>
                      <div>
                        <h4 className="mb-2 text-sm font-semibold">
                          Location <span className="font-light">(optional)</span>
                        </h4>
                        <Dropdown
                          options={providersDropdownOptions}
                          value={entryData.provider}
                          onChange={(event: DropdownChangeEvent) => handleChangeEntryData('provider', event.value)}
                          optionLabel="name"
                          className="w-full !border-gray-300 [&_.p-dropdown-label]:truncate [&_.p-dropdown-trigger]:!text-gray-400"
                        />
                      </div>
                      <div>
                        <h4 className="mb-2 text-sm font-semibold">Pages</h4>
                        <InputText
                          value={entryData.pages}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChangeEntryData('pages', event.target.value)
                          }
                          onFocus={() => setIsPagesInputFocused(true)}
                          onBlur={() => setIsPagesInputFocused(false)}
                          className={classNames('!h-[32px] w-full !text-[14px]', {
                            '!border-fletchers-red-500': !isPagesInputFocused && isPagesInputError,
                          })}
                        />
                      </div>
                    </div>

                    {/* Middle column: Extracted text with symbol picker.
                        Unified field — wrapper owns the border and focus
                        ring; toolbar (Ω) and textarea inside are borderless
                        so they read as one input. Same pattern as the
                        Add-to-Chronology dialog's Extract field. */}
                    <div className="flex flex-col">
                      <h4 className="mb-2 text-sm font-semibold">Extracted text</h4>
                      <div className="focus-within:border-fletchers-gray-200 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-gray-300 bg-white transition-colors">
                        <SymbolPickerToolbar
                          bordered={false}
                          onInsertSymbol={insertSymbol}
                          dictation={{
                            isSupported: extractDictation.browserSupportsSpeechRecognition,
                            isListening: extractDictation.listening,
                            isBusy: extractDictation.isFinalizing,
                            onStart: extractDictation.startListening,
                            onCancel: extractDictation.cancelListening,
                            onConfirm: extractDictation.confirmListening,
                          }}
                        />
                        <InputTextarea
                          value={entryData.summary}
                          readOnly={extractDictation.listening}
                          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                            handleChangeEntryData('summary', event.target.value)
                          }
                          onFocus={(event) => {
                            extractTextRef.current = event.target as HTMLTextAreaElement;
                          }}
                          className="h-full w-full resize-none !rounded-none !border-0 !pt-0 !shadow-none !ring-0 !outline-none focus:!border-0 focus:!shadow-none focus:!ring-0"
                        />
                      </div>
                    </div>

                    {/* Right column: Comment + buttons — same unified field
                        pattern as the Extract column. */}
                    <div className="flex w-[240px] flex-col">
                      <h4 className="mb-2 text-sm font-semibold">
                        Comment <span className="font-light">(optional)</span>
                      </h4>
                      <div className="focus-within:border-fletchers-gray-200 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[4px] border border-gray-300 bg-white transition-colors">
                        <SymbolPickerToolbar
                          bordered={false}
                          onInsertSymbol={insertSymbolIntoComment}
                          dictation={{
                            isSupported: commentDictation.browserSupportsSpeechRecognition,
                            isListening: commentDictation.listening,
                            isBusy: commentDictation.isFinalizing,
                            onStart: commentDictation.startListening,
                            onCancel: commentDictation.cancelListening,
                            onConfirm: commentDictation.confirmListening,
                          }}
                        />
                        <InputTextarea
                          value={entryData.comment}
                          readOnly={commentDictation.listening}
                          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                            handleChangeEntryData('comment', event.target.value)
                          }
                          onFocus={(event) => {
                            commentTextRef.current = event.target as HTMLTextAreaElement;
                          }}
                          className="h-full w-full resize-none !rounded-none !border-0 !pt-0 !shadow-none !ring-0 !outline-none focus:!border-0 focus:!shadow-none focus:!ring-0"
                        />
                      </div>
                      <div className="mt-4 flex justify-end gap-2">
                        <OutlineButton onClick={newEntryIndex ? handleCancelCreateNewEntry : handleCancelEditEntry}>
                          Cancel
                        </OutlineButton>
                        <GreenButton
                          disabled={!isValidEntryData}
                          onClick={newEntryIndex ? handleCreateNewEntry : handleSaveExistingEntry}
                        >
                          {newEntryIndex ? 'Create' : 'Confirm'}
                        </GreenButton>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // Read-only mode rendering
            return (
              <div className="px-3">
                {dateHeaderText && renderDateHeaderWithButton(dateHeaderText, entry.id)}
                <div
                  key={entry.id}
                  onClick={() => {
                    if (isEditTimelineChronology && newEntryIndex && isValidEntryData) {
                      handleCreateNewEntry();
                      return;
                    }
                    if (isEditTimelineChronology && editEntryIndex && isValidEntryData) {
                      handleSaveExistingEntry();
                      return;
                    }
                    if (isEditTimelineChronology && !newEntryIndex && !editEntryIndex) {
                      handleEditEntryData(entry, index);
                    }
                  }}
                  className={classNames(
                    'group flex text-[14px] transition-shadow duration-200',
                    'border-fletchers-gray-110 overflow-auto rounded-lg border border-l-[3px] bg-white hover:shadow-[0_0_8px_rgba(0,0,0,0.15)]',
                    {
                      'opacity-[0.5]': newEntryIndex || editEntryIndex,
                      'px-6 py-[16px]': entry.summary,
                      'border-l-fletchers-green-300': entry.summary && !entry.containsAdditionalRecords,
                      'border-l-[#3BB8C9]': entry.summary && entry.containsAdditionalRecords,
                      '!border-fletchers-red-500 bg-fletchers-gray-50 border-l-[3px] p-5.75': !entry.summary,
                      // Highlights the destination row after a cross-tab
                      // jump. The `-a` / `-b` variants are identical
                      // animations under different names — alternating
                      // them each trigger (via `flashState.token`
                      // parity) is what makes CSS restart the animation
                      // when the same entry is re-clicked. Cleared by
                      // the parent after ~6s.
                      'animate-chronology-entry-flash-a': flashState?.id === entry.id && flashState.token % 2 === 0,
                      'animate-chronology-entry-flash-b': flashState?.id === entry.id && flashState.token % 2 === 1,
                    },
                  )}
                >
                  {/* Main content */}
                  <div className="flex-1">
                    {/* Top row: checkbox, time, date, location, pages */}
                    <div className="flex items-center gap-[36px]">
                      <Checkbox
                        checked={selectedEntryIds.includes(entry.id)}
                        onChange={(event: CheckboxChangeEvent) => handleChangeSelectedEntryIds(entry.id, event.checked)}
                        onClick={(event: MouseEvent<HTMLInputElement>) => event.stopPropagation()}
                        className={classNames('mr-[-24px]', {
                          'pointer-events-none': newEntryIndex || editEntryIndex,
                        })}
                      />
                      <span className="whitespace-nowrap">
                        {entry.time?.substring(0, 5) || ''}{' '}
                        <span className="font-semibold">{entry.date && format(new Date(entry.date), 'd MMM y')}</span>
                      </span>
                      {entry.providerName && <span className="text-gray-600">{entry.providerName}</span>}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>
                          {sortPagesByBundleType(entry.pages, activeBundlePageType).filter((page) =>
                            activeBundlePageType === ActiveBundlePageType.LIGHT
                              ? 'lightBundlePageNumber' in page && page.lightBundlePageNumber !== null
                              : 'fullBundlePageNumber' in page && page.fullBundlePageNumber !== null,
                          ).length === 1
                            ? 'Page'
                            : 'Pages'}
                        </span>
                        {sortPagesByBundleType(entry.pages, activeBundlePageType).map((page) => {
                          if (
                            activeBundlePageType === ActiveBundlePageType.LIGHT &&
                            'lightBundlePageNumber' in page &&
                            page.lightBundlePageNumber !== null
                          ) {
                            return (
                              <button
                                key={page.pageId}
                                type="button"
                                disabled={!isViewBundles}
                                className={classNames(
                                  'bg-fletchers-gray-50 flex min-w-[20px] items-center justify-center rounded-[4px] px-[6px] py-[2px] text-xs font-medium text-gray-700 focus:outline-none enabled:cursor-pointer enabled:hover:bg-gray-200 enabled:focus:bg-gray-200',
                                  {
                                    'pointer-events-none': newEntryIndex || editEntryIndex,
                                  },
                                )}
                                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                  event.stopPropagation();
                                  handlePageClick({
                                    activeBundlePageType: ActiveBundlePageType.LIGHT,
                                    pageId: page.pageId,
                                  });
                                }}
                              >
                                {page.lightBundlePageNumber}
                              </button>
                            );
                          }

                          if (
                            activeBundlePageType === ActiveBundlePageType.FULL &&
                            'fullBundlePageNumber' in page &&
                            page.fullBundlePageNumber !== null
                          ) {
                            return (
                              <button
                                key={page.pageId}
                                type="button"
                                disabled={!isViewBundles}
                                className={classNames(
                                  'bg-fletchers-gray-50 flex min-w-[20px] items-center justify-center rounded-[4px] px-[6px] py-[2px] text-xs font-medium text-gray-700 focus:outline-none enabled:cursor-pointer enabled:hover:bg-gray-200 enabled:focus:bg-gray-200',
                                  {
                                    'pointer-events-none': newEntryIndex || editEntryIndex,
                                  },
                                )}
                                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                  event.stopPropagation();
                                  handlePageClick({
                                    activeBundlePageType: ActiveBundlePageType.FULL,
                                    pageId: page.pageId,
                                  });
                                }}
                              >
                                {page.fullBundlePageNumber}
                              </button>
                            );
                          }

                          return null;
                        })}
                      </div>
                    </div>

                    <div className="my-[16px] border-t border-gray-200" />

                    {/* Second row: extract and comment */}
                    <div className="flex gap-6">
                      <div className="flex-[3]">
                        <h4 className="mb-2 flex items-center gap-1.5 text-gray-600">
                          <QuoteOutlineIcon className="h-[13px] w-[13px]" />
                          Extracted text
                        </h4>
                        <div className="whitespace-pre-wrap">{entry.summary}</div>
                      </div>

                      {entry.comment && (
                        <div className="flex-1">
                          <h4 className="mb-2 flex items-center gap-1.5 text-gray-600">
                            <CommentIcon className="h-[14px] w-[14px]" />
                            Comment
                          </h4>
                          <div className="rounded border border-gray-200 bg-gray-50 p-3 whitespace-pre-wrap">
                            {entry.comment}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right actions panel */}
                  <div
                    className="-mr-[8px] ml-[16px] flex flex-col items-center gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
                  >
                    <Button
                      outlined
                      icon={<TrashIcon className="h-[14px] w-[14px] text-zinc-950" />}
                      className={classNames('!h-[32px] !w-[32px] !px-[9px] !py-[9px] hover:!bg-gray-50', {
                        'pointer-events-none': newEntryIndex || editEntryIndex,
                      })}
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        handleTimelineChronologyDelete(TIMELINE_CHRONOLOGY_LIST_DELETE_ITEM, [entry.id]);
                      }}
                    />

                    {currentIndex !== undefined && shouldShowArrows(entry, currentIndex) && (
                      <>
                        <Button
                          outlined
                          disabled={!shouldShowUpArrow(entry, currentIndex)}
                          className={classNames('!h-[32px] !w-[32px] !px-[7px] !py-[7px] hover:!bg-gray-50', {
                            'pointer-events-none': newEntryIndex || editEntryIndex,
                          })}
                          icon={<ArrowUpIcon className="h-[18px] w-[18px]" />}
                          onClick={async (event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            if (currentIndex !== undefined && currentIndex > 0) {
                              const targetEntry = currentListEntries[currentIndex - 1];
                              await handleReorder(entry.id, targetEntry.id, 'before');
                            }
                          }}
                        />

                        <Button
                          outlined
                          disabled={!shouldShowDownArrow(entry, currentIndex)}
                          className={classNames('!h-[32px] !w-[32px] !px-[7px] !py-[7px] hover:!bg-gray-50', {
                            'pointer-events-none': newEntryIndex || editEntryIndex,
                          })}
                          icon={<ArrowDown className="h-[18px] w-[18px]" />}
                          onClick={async (event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            if (currentIndex !== undefined && currentIndex < currentListEntries.length - 1) {
                              const targetEntry = currentListEntries[currentIndex + 1];
                              await handleReorder(entry.id, targetEntry.id, 'after');
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          }}
          renderInsertZone={() => {
            return <div className="h-[12px] w-full" />;
          }}
        />
      </div>
    </>
  );
});
